import classNames from 'classnames';
import { extent, scaleBand, scaleLinear } from 'd3';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import {
  BAR_END_HOVER_SLOP,
  CHART_HEADER_LABEL_HEIGHT,
  CHART_LABEL_GAP,
  NEW_THEME_BAR_GAP,
  NEW_THEME_BAR_THICKNESS,
  ReplotChartNames,
} from '../../constants';
import { type CommonPlotProps, FormatType } from '../../types';
import {
  findNearestBandIndex,
  getDimmedPlotColor,
  getFormatter,
  getMaxTextWidth,
  useChartTheme,
  useResizeObserver,
  VOLUME_NEGATIVE_COLOR,
  VOLUME_POSITIVE_COLOR,
} from '../../utilities';
import { plotTestId } from '../../utilities/plotTestId';
import { AxisLabel } from '../axis/axis-label';
import axisLabelStyles from '../axis/axis-label.module.css';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import {
  getCursorPosition,
  PlotTooltip,
  type TooltipPosition,
  useTooltipController,
} from '../plot-tooltip';
import styles from './diverging-bar-chart.module.css';

export type DivergingBarLayout = { centerX: number };

const Dimensions = {
  BarLeftPadding: 4,
  paddingInner: 0.4,
  paddingOuter: 0.4,
};

export type DivergingBarData = {
  label: string;
  leftValue: number | null;
  rightValue: number | null;
  leftLabel: string;
  rightLabel: string;
  leftId?: string | null;
  rightId?: string | null;
};

export type DivergingBarChartProps = {
  data: DivergingBarData[];
  leftColor?: string;
  rightColor?: string;
  showBarLabels?: boolean;
  tooltipContent?: (
    data: DivergingBarData,
    side: 'left' | 'right'
  ) => JSX.Element;
  valueLabel?: (data: DivergingBarData, side: 'left' | 'right') => string;
  onLayout?: (layout: DivergingBarLayout) => void;
  footer?: (layout: DivergingBarLayout) => ReactNode;
  footerHeight?: number;
  /** When true (default), data is sorted by net difference (rightValue + leftValue) descending. Set to false when parent provides pre-sorted data. */
  sortData?: boolean;
  /** Optional labels shown above the left and right bar areas (e.g. entity names in dual-view comparison). */
  leftHeaderLabel?: string;
  rightHeaderLabel?: string;
  /** Reserve space at top so bar area aligns with another chart that has a header (e.g. dual-view). */
  topOffset?: number;
  /** When false, y-axis row labels are not rendered (e.g. second chart in dual-view). Default true. */
  showYAxisLabels?: boolean;
  /** Optional fill-opacity for bar rects (0–1). Lets callers visually de-emphasize a chart in a dual-view. */
  barOpacity?: number;
  /** Fraction of the band height to use for bar thickness (0–1). Defaults to 1. Use a smaller value to visually de-emphasize bars while keeping them opaque. */
  barHeightRatio?: number;
  /** Maximum fraction of the chart width that the y-axis row labels can consume (0–1). Defaults to 0.35. */
  maxLabelWidthRatio?: number;
  /** Pixel floor for the label-width cap: labels may use up to max(minLabelWidth, width × maxLabelWidthRatio). */
  minLabelWidth?: number;
  labelWidth?: number;
  labelChartGap?: number;
  barThickness?: number;
  barGap?: number;
  /**
   * New-theme vertical band layout. 'centered' (default) caps bar thickness
   * and centres the block; 'fill' scales bars and gaps proportionally to use
   * the available height, keeping ~half a gap of outer margin.
   */
  verticalLayout?: 'centered' | 'fill';
  /** Square by default, matching the bar / stacked-bar / box marks. */
  barCornerRadius?: number;
  centerGap?: number;
  barLabelPosition?: 'auto' | 'inside' | 'outside';
  barLabelColor?: string;
  labelReserveRatio?: number;
  activeLabel?: string | null;
  onHoveredLabelChange?: (label: string | null) => void;
} & CommonPlotProps;

const DEFAULT_FOOTER_HEIGHT = 25;
/** Height of the optional header row (entity labels). Export for dual-view alignment. */
export const DIVERGING_BAR_HEADER_LABEL_HEIGHT = CHART_HEADER_LABEL_HEIGHT;
/** New-theme reserved gap between the header/footer labels and the bars. Exported for dual-view topOffset alignment. */
export const DIVERGING_BAR_LABEL_GAP = CHART_LABEL_GAP;

export const DivergingBarChart = ({
  className,
  data,
  loading = false,
  format = FormatType.PERCENTAGE,
  leftColor: leftColorProp,
  rightColor: rightColorProp,
  showBarLabels = false,
  tooltipContent,
  valueLabel,
  onLayout,
  footer,
  footerHeight = DEFAULT_FOOTER_HEIGHT,
  sortData = true,
  leftHeaderLabel,
  rightHeaderLabel,
  topOffset = 0,
  showYAxisLabels = true,
  barOpacity,
  barHeightRatio = 1,
  maxLabelWidthRatio = 0.35,
  minLabelWidth,
  labelWidth: labelWidthProp,
  labelChartGap = Dimensions.BarLeftPadding,
  barThickness = NEW_THEME_BAR_THICKNESS,
  barGap = NEW_THEME_BAR_GAP,
  verticalLayout = 'centered',
  barCornerRadius = 0,
  centerGap = 0,
  barLabelPosition = 'auto',
  barLabelColor,
  labelReserveRatio = 0.4,
  activeLabel,
  onHoveredLabelChange,
}: DivergingBarChartProps) => {
  const { containerRef, width, height } = useResizeObserver();
  const { theme } = useChartTheme();

  const leftColor = leftColorProp ?? VOLUME_NEGATIVE_COLOR;
  const rightColor = rightColorProp ?? VOLUME_POSITIVE_COLOR;

  const hasHeaderLabels =
    !!leftHeaderLabel?.trim() || !!rightHeaderLabel?.trim();
  const headerHeight = hasHeaderLabels ? DIVERGING_BAR_HEADER_LABEL_HEIGHT : 0;
  const labelGap = DIVERGING_BAR_LABEL_GAP;
  const contentHeight = footer
    ? Math.max(0, height - footerHeight - labelGap)
    : height;
  const plotHeight = Math.max(
    0,
    contentHeight - headerHeight - (hasHeaderLabels ? labelGap : 0) - topOffset
  );
  const labelWidth = (() => {
    if (!showYAxisLabels || data.length === 0) return 0;
    if (labelWidthProp !== undefined) return labelWidthProp;
    return Math.min(
      getMaxTextWidth({ texts: data.map((d) => d.label) }),
      Math.max(minLabelWidth ?? 0, width * maxLabelWidthRatio)
    );
  })();
  const margin = labelWidth + labelChartGap;
  const plotWidth = Math.max(0, width - margin);
  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;

  const centerX = margin + plotWidth / 2;
  useEffect(() => {
    onLayout?.({ centerX });
  }, [onLayout, centerX]);

  const sortedData = sortData
    ? [...data].sort((a, b) => {
        const aDiff = (a.rightValue ?? 0) + (a.leftValue ?? 0);
        const bDiff = (b.rightValue ?? 0) + (b.leftValue ?? 0);
        return bDiff - aDiff;
      })
    : [...data];

  const barCount = sortedData.length;
  const newThemePaddingInner = barGap / (barThickness + barGap);
  const paddingInner = newThemePaddingInner;
  const paddingOuter = (() => {
    if (barCount === 0 || plotHeight <= 0) {
      return Dimensions.paddingOuter;
    }
    // 'fill': no thickness cap — bars and gaps scale proportionally with the
    // available height, leaving half an inter-bar gap of outer margin.
    if (verticalLayout === 'fill') {
      return newThemePaddingInner / 2;
    }
    return Math.max(
      Dimensions.paddingOuter,
      ((plotHeight * (1 - newThemePaddingInner)) / barThickness -
        barCount +
        newThemePaddingInner) /
        2
    );
  })();
  const yScale = scaleBand()
    .domain(sortedData.map((d) => d.label))
    .range([0, plotHeight])
    .paddingInner(paddingInner)
    .paddingOuter(paddingOuter);

  const allValues = sortedData
    .flatMap((d) => [d.leftValue, d.rightValue])
    .filter((v): v is number => v !== null);
  const [minValue, maxValue] = extent(allValues);

  const formatValue = getFormatter(format);
  const labelPadding = 6;

  const labelReserve = (() => {
    if (!showBarLabels) return 0;
    const labelTexts = sortedData.flatMap((d) => {
      const texts: string[] = [];
      if (typeof d.leftValue === 'number') {
        texts.push(
          valueLabel ? valueLabel(d, 'left') : formatValue(d.leftValue)
        );
      }
      if (typeof d.rightValue === 'number') {
        texts.push(
          valueLabel ? valueLabel(d, 'right') : formatValue(d.rightValue)
        );
      }
      return texts;
    });
    if (labelTexts.length === 0) return 0;
    const maxLabelWidth = getMaxTextWidth({
      texts: labelTexts,
      fontSize: 12,
    });
    const reserve = maxLabelWidth + labelPadding;
    return Math.min(reserve, plotWidth * labelReserveRatio);
  })();

  const valueScale = (() => {
    if (minValue === undefined || maxValue === undefined) return null;
    const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
    return scaleLinear(
      [-absMax, absMax],
      [labelReserve, Math.max(labelReserve, plotWidth - labelReserve)]
    );
  })();

  // The plot centre splits each row's hover into its left/right halves.
  const rowStarts = sortedData.map((d) => yScale(d.label));
  const rowSlop = Math.max(0, (yScale.step() - yScale.bandwidth()) / 2);
  const [hovered, setHovered] = useState<{
    rowIndex: number;
    side: 'left' | 'right';
  } | null>(null);
  const [cursor, setCursor] = useState<TooltipPosition | null>(null);
  const { isVisible, gProps } = useTooltipController({
    onHover: ([px, py], event) => {
      setCursor(getCursorPosition(event));
      const rowIndex = findNearestBandIndex(
        rowStarts,
        yScale.bandwidth(),
        py,
        rowSlop
      );
      const item = rowIndex === null ? undefined : sortedData[rowIndex];
      if (rowIndex === null || item === undefined || !valueScale) {
        setHovered(null);
        return;
      }
      const center = valueScale(0);
      const side = px < center ? 'left' : 'right';
      const value = side === 'left' ? item.leftValue : item.rightValue;
      if (typeof value !== 'number') {
        setHovered(null);
        return;
      }
      const scaled = valueScale(value);
      const withinBar =
        side === 'left'
          ? px >= Math.min(scaled, center) - BAR_END_HOVER_SLOP
          : px <= Math.max(scaled, center) + BAR_END_HOVER_SLOP;
      setHovered(withinBar ? { rowIndex, side } : null);
    },
  });
  useEffect(() => {
    if (!isVisible) {
      setHovered(null);
      setCursor(null);
    }
  }, [isVisible]);

  const hoveredItem =
    hovered === null ? null : (sortedData[hovered.rowIndex] ?? null);

  const hoveredLabel = hoveredItem?.label ?? null;
  useEffect(() => {
    onHoveredLabelChange?.(hoveredLabel);
  }, [onHoveredLabelChange, hoveredLabel]);
  const highlightedLabel = hoveredLabel ?? activeLabel ?? null;

  const dimmedLeftColor = getDimmedPlotColor(leftColor, theme);
  const dimmedRightColor = getDimmedPlotColor(rightColor, theme);

  const renderBar = (item: DivergingBarData) => {
    const y = yScale(item.label);
    if (y === undefined || !valueScale) return null;

    const bandHeight = yScale.bandwidth();
    const barHeight = bandHeight * barHeightRatio;
    const barY = y + (bandHeight - barHeight) / 2;

    const centerX = valueScale(0);
    const leftValue = item.leftValue;
    const rightValue = item.rightValue;
    const hasLeft = typeof leftValue === 'number';
    const hasRight = typeof rightValue === 'number';

    const leftX = hasLeft ? valueScale(leftValue) : centerX;
    const rightX = hasRight ? valueScale(rightValue) : centerX;

    const leftBarWidth = hasLeft ? Math.abs(centerX - leftX) : 0;
    const rightBarWidth = hasRight ? Math.abs(rightX - centerX) : 0;

    const leftBarWidthForRect =
      hasLeft && (leftValue === 0 || leftBarWidth === 0)
        ? Math.max(1, leftBarWidth)
        : leftBarWidth;
    const rightBarWidthForRect =
      hasRight && (rightValue === 0 || rightBarWidth === 0)
        ? Math.max(1, rightBarWidth)
        : rightBarWidth;

    const leftBarRectX = (() => {
      if (!hasLeft) return centerX;
      if (leftValue === 0 || leftBarWidth === 0) {
        return centerX - leftBarWidthForRect;
      }
      return Math.min(leftX, centerX);
    })();

    const halfGap = centerGap / 2;
    const leftRectWidth =
      leftBarWidthForRect > halfGap
        ? leftBarWidthForRect - halfGap
        : leftBarWidthForRect;
    // Bars shorter than halfGap keep their full width, so shift them left
    // instead — the visible centre gap stays centerGap even for tiny bars.
    const leftRectX =
      leftBarWidthForRect > halfGap ? leftBarRectX : leftBarRectX - halfGap;
    const rightRectX = centerX + halfGap;
    const rightRectWidth =
      rightBarWidthForRect > halfGap
        ? rightBarWidthForRect - halfGap
        : rightBarWidthForRect;

    const leftBarLabel = (() => {
      if (!hasLeft) return '';
      if (valueLabel) return valueLabel(item, 'left');
      return formatValue(leftValue);
    })();

    const rightBarLabel = (() => {
      if (!hasRight) return '';
      if (valueLabel) return valueLabel(item, 'right');
      return formatValue(rightValue);
    })();

    const isDimmed =
      highlightedLabel !== null && highlightedLabel !== item.label;

    return (
      <g key={item.label}>
        {hasLeft && (
          <rect
            x={leftRectX}
            y={barY}
            width={leftRectWidth}
            height={barHeight}
            rx={barCornerRadius}
            fill={leftColor}
            fillOpacity={barOpacity}
            style={{ '--replots-dim-color': dimmedLeftColor } as CSSProperties}
            className={classNames(styles.bar, isDimmed && styles.barDimmed)}
            data-testid="diverging-bar-chart-bar-left"
          />
        )}
        {showBarLabels &&
          hasLeft &&
          (() => {
            const labelWidthPx = leftBarLabel.length * 6;
            const fitsInside = leftBarWidthForRect > labelWidthPx + 8;
            const placeInside =
              barLabelPosition === 'auto'
                ? fitsInside
                : barLabelPosition === 'inside';

            let x: number;
            let textAnchor: 'start' | 'end';
            let fill: string;

            const leftEdgeX =
              Math.min(leftX, centerX) -
              (leftBarWidthForRect > halfGap ? 0 : halfGap);
            if (placeInside) {
              x = leftEdgeX + labelPadding;
              textAnchor = 'start';
              fill = 'white';
            } else {
              x = leftEdgeX - labelPadding;
              textAnchor = 'end';
              fill = barLabelColor ?? leftColor;
            }

            return (
              <text
                x={x}
                y={y + yScale.bandwidth() / 2}
                textAnchor={textAnchor}
                fontSize={12}
                fill={fill}
                dominantBaseline="middle"
                pointerEvents="none"
                className={isDimmed ? styles.barLabelDimmed : undefined}
              >
                {leftBarLabel}
              </text>
            );
          })()}
        {hasRight && (
          <rect
            x={rightRectX}
            y={barY}
            width={rightRectWidth}
            height={barHeight}
            rx={barCornerRadius}
            fill={rightColor}
            fillOpacity={barOpacity}
            style={{ '--replots-dim-color': dimmedRightColor } as CSSProperties}
            className={classNames(styles.bar, isDimmed && styles.barDimmed)}
            data-testid="diverging-bar-chart-bar-right"
          />
        )}
        {showBarLabels &&
          hasRight &&
          (() => {
            const labelWidthPx = rightBarLabel.length * 6;
            const fitsInside = rightBarWidthForRect > labelWidthPx + 8;
            const placeInside =
              barLabelPosition === 'auto'
                ? fitsInside
                : barLabelPosition === 'inside';

            let x: number;
            let textAnchor: 'start' | 'end';
            let fill: string;

            if (placeInside) {
              x = Math.max(centerX, rightX) - labelPadding;
              textAnchor = 'end';
              fill = 'white';
            } else {
              x = Math.max(centerX, rightX) + labelPadding;
              textAnchor = 'start';
              fill = barLabelColor ?? rightColor;
            }

            return (
              <text
                x={x}
                y={y + yScale.bandwidth() / 2}
                textAnchor={textAnchor}
                fontSize={12}
                fill={fill}
                dominantBaseline="middle"
                pointerEvents="none"
                className={isDimmed ? styles.barLabelDimmed : undefined}
              >
                {rightBarLabel}
              </text>
            );
          })()}
      </g>
    );
  };

  const renderAxisLabel = (label: string) => {
    if (!showYAxisLabels) return null;
    const y = yScale(label);
    if (y === undefined) return null;

    return (
      <AxisLabel
        key={label}
        y={y + yScale.bandwidth() / 2}
        width={labelWidth}
        fontSize={12}
        label={label}
        availableHeight={yScale.bandwidth()}
        testId="diverging-bar-chart-axis-label"
      />
    );
  };

  const layout: DivergingBarLayout = { centerX };

  const firstBandTop =
    sortedData.length > 0 ? (yScale(sortedData[0]?.label ?? '') ?? 0) : 0;
  // The reserved labelGap keeps header/footer DIVERGING_BAR_LABEL_GAP away
  // from the bar block; the shifts pin them to it when centred bars leave
  // extra outer space.
  const headerShift = firstBandTop;
  const lastBandBottom =
    sortedData.length > 0
      ? (yScale(sortedData[sortedData.length - 1]?.label ?? '') ?? 0) +
        yScale.bandwidth()
      : 0;
  const footerLift =
    sortedData.length > 0 ? Math.max(0, plotHeight - lastBandBottom) : 0;

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, className)}
      style={
        footer || hasHeaderLabels || topOffset > 0
          ? {
              display: 'flex',
              flexDirection: 'column',
            }
          : undefined
      }
      data-testid={plotTestId(ReplotChartNames.DivergingBarChart)}
    >
      {topOffset > 0 && (
        <div
          style={{
            flexShrink: 0,
            height: topOffset,
            width: '100%',
          }}
        />
      )}
      {hasHeaderLabels && (
        <div
          className={classNames(
            styles.headerRow,
            !isPlotSizeValid && styles.pending
          )}
          style={{
            height: headerHeight,
            paddingLeft: margin,
            ...(labelGap > 0 && { marginBottom: labelGap }),
            ...(headerShift > 0 && {
              transform: `translateY(${headerShift}px)`,
            }),
          }}
        >
          <div className={styles.headerGrid}>
            <span
              className={classNames(
                axisLabelStyles.yAxisLabel,
                styles.headerLabel,
                styles.headerLabelLeft
              )}
              title={leftHeaderLabel ?? ''}
            >
              {leftHeaderLabel ?? ''}
            </span>
            <span
              className={classNames(
                axisLabelStyles.yAxisLabel,
                styles.headerLabel,
                styles.headerLabelRight
              )}
              title={rightHeaderLabel ?? ''}
            >
              {rightHeaderLabel ?? ''}
            </span>
          </div>
        </div>
      )}
      <PlotLoadWrapper loading={loading} noData={sortedData.length === 0}>
        <div
          style={{
            width: '100%',
            flex:
              footer || hasHeaderLabels || topOffset > 0
                ? '1 1 auto'
                : undefined,
            minHeight:
              footer || hasHeaderLabels || topOffset > 0 ? 0 : undefined,
          }}
        >
          <svg width="100%" height={plotHeight}>
            <g id="y-axis">
              {isPlotSizeValid && yScale.domain().map(renderAxisLabel)}
            </g>
            <g id="chart" transform={`translate(${margin}, 0)`} {...gProps}>
              {isPlotSizeValid && (
                <rect
                  className={styles.hoverOverlay}
                  x={0}
                  y={0}
                  width={plotWidth}
                  height={plotHeight}
                  fill="transparent"
                />
              )}
              {isPlotSizeValid && valueScale && sortedData.map(renderBar)}
            </g>
          </svg>
          {hovered !== null && hoveredItem !== null && cursor !== null && (
            <PlotTooltip
              isVisible={isVisible}
              x={cursor.x}
              y={cursor.y}
              placement="vertical"
              space="viewport"
            >
              {tooltipContent ? (
                tooltipContent(hoveredItem, hovered.side)
              ) : (
                <div className={styles.tooltipContent}>
                  <div className={styles.tooltipTitle}>{hoveredItem.label}</div>
                  <div>
                    {hovered.side === 'left'
                      ? `${hoveredItem.leftLabel}: ${formatValue(hoveredItem.leftValue ?? 0)}`
                      : `${hoveredItem.rightLabel}: ${formatValue(hoveredItem.rightValue ?? 0)}`}
                  </div>
                </div>
              )}
            </PlotTooltip>
          )}
        </div>
      </PlotLoadWrapper>
      {footer && (
        <div
          style={{
            flexShrink: 0,
            height: footerHeight,
            width: '100%',
            ...(labelGap > 0 && { marginTop: labelGap }),
            ...(footerLift > 0 && {
              transform: `translateY(${-footerLift}px)`,
            }),
          }}
        >
          {footer(layout)}
        </div>
      )}
    </div>
  );
};
